/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Details/Actions/ServiceEndpointActionsCreator" />
import { ServiceEndpointActionsHub } from "DistributedTaskUI/ServiceEndpoints/Details/Actions/ServiceEndpointActionsHub";
import { ServiceEndpointsSource, IServiceEndpointDetails } from "DistributedTaskUI/ServiceEndpoints/Details/Source/ServiceEndpointsSource";
import { ActionCreatorBase } from "DistributedTask/Flux/Actions";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { ServiceEndpoint, ServiceEndpointRequest, ServiceEndpointAuthenticationScheme, ServiceEndpointRequestResult } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import * as EndpointConstants  from "ServiceEndpoints/Client/Constants/ServiceEndpoint";
import { ServiceEndpointOperationStatus, EndpointSchemeConversionSuccessful } from "DistributedTaskUI/ServiceEndpoints/Details/Constants";
import * as DelayedFunction from "DistributedTask/Common/AadAuthorizer/DelayedFunction";
import { IVssPageContext } from "VSS/Platform/Context";
import * as Utils_String from "VSS/Core/Util/String";

export class ServiceEndpointActionsCreator extends ActionCreatorBase {
    constructor(context: IVssPageContext) {
        super();
        this._actions = ActionsHubManager.GetActionsHub<ServiceEndpointActionsHub>(ServiceEndpointActionsHub);
        this._source = ServiceEndpointsSource.getInstance(context);
        this._monitoredSchemeConversions = [];
    }

    public initialize(): void {}

    public static getKey(): string {
        return "service-endpoints-section-actionscreator";
    }

    public getServiceEndpoint(endpointId: string) {
        this._source.getServiceEndpointsHubData(endpointId).then((serviceEndpointsHubData: IServiceEndpointDetails) => {
            if (!serviceEndpointsHubData || !serviceEndpointsHubData.serviceEndpoint) {
                this._actions.setServiceEndpointNotFoundFlag.invoke(true);
            } else {
                let creatorId = serviceEndpointsHubData.serviceEndpoint.createdBy.id;
                this._source.getIdentityRefWithEmail(creatorId).then((identities) => {
                    if (identities && identities.length > 0) {
                        this._actions.setServiceEndpointOwnerEmail.invoke(identities[0].preferredEmailAddress);
                    }
                });

                this._actions.initializeServiceEndpointHubData.invoke(serviceEndpointsHubData);
            }
        });
    }

    public getServiceEndpointDetails(endpointId: string): Promise<IServiceEndpointDetails> {
        return this._source.getServiceEndpointsHubData(endpointId);
    }

    public executeServiceEndpointRequest(serviceEndpointRequest: ServiceEndpointRequest, endpointId: string): Promise<ServiceEndpointRequestResult> {
        return this._source.executeServiceEndpointRequest(serviceEndpointRequest, endpointId);
    }

    public deleteServiceEndpoint(endpointId: string, deep?: boolean): Promise<void> {
        return this._source.deleteServiceEndpoint(endpointId, deep);
    }
    
    public disableServiceEndpoint(endpointId: string, endpoint: ServiceEndpoint): Promise<ServiceEndpoint> {
        return this._source.disableServiceEndpoint(endpointId, endpoint);
    }
    public enableServiceEndpoint(endpointId: string, endpoint: ServiceEndpoint): Promise<ServiceEndpoint> {
        return this._source.enableServiceEndpoint(endpointId, endpoint);
    }

    public checkManageEndpointPermission(endpointId: string, managePermission: number) {
        return this._source.checkManageEndpointPermission(endpointId, managePermission);
    }

    public checkManageProjectSharingPermission(endpointId: string, managePermission: number) {
        return this._source.checkManageProjectSharingPermission(endpointId, managePermission);
    }

    public checkProjectLevelManagePermission(managePermission: number) {
        return this._source.checkProjectLevelManagePermission(managePermission);
    }

    public convertServiceEndpointAuthenticationSchemeToSecret(
        serviceEndpoint: ServiceEndpoint,
        scheme: ServiceEndpointAuthenticationScheme,
        values: { [key: string]: string }
    ) {
        this._actions.setEditingEnabled.invoke(false);
        return this._source
            .convertServiceEndpointAuthenticationSchemeToSecret(serviceEndpoint, scheme, values)
            .then((endpoint) => {
                const endpointBeingUpdated = endpoint as ServiceEndpoint;
                if (endpointBeingUpdated.isReady) {
                    this.getServiceEndpoint(endpointBeingUpdated.id);
                } else {
                    this._monitorEndpointSchemeConversionProgress(endpointBeingUpdated.id, 500, 1000);
                }
            });
    }

    public convertServiceEndpointAuthenticationScheme(serviceEndpoint: ServiceEndpoint, scheme: ServiceEndpointAuthenticationScheme) {
        this._actions.setEditingEnabled.invoke(false);
        this._actions.updateServiceEndpointAuthenticationScheme.invoke(
            EndpointConstants.AzureEndpointConstants.ConvertAuthenticationSchemeInProgressStatus
        );
        this._source
            .convertServiceEndpointAuthenticationScheme(serviceEndpoint, scheme)
            .catch(() => {
                this._actions.updateServiceEndpointAuthenticationScheme.invoke(
                    EndpointConstants.AzureEndpointConstants.ConvertAuthenticationSchemeFailedStatus
                );
            })
            .then((endpoint) => {
                const endpointBeingUpdated = endpoint as ServiceEndpoint;
                if (endpointBeingUpdated.authorization.scheme === scheme.scheme) {
                    this._actions.updateServiceEndpointAuthenticationScheme.invoke(EndpointSchemeConversionSuccessful);
                    this.getServiceEndpoint(endpointBeingUpdated.id);
                } else {
                    this._monitorEndpointSchemeConversionProgress(endpointBeingUpdated.id, 65_000, 30_000, () => {
                        this._actions.updateServiceEndpointAuthenticationScheme.invoke(EndpointSchemeConversionSuccessful);
                    });
                }
            });
    }

    public monitorSchemeConversionProgress(endpointId: string) {
        this._monitorEndpointSchemeConversionProgress(endpointId, 1_000, 10_000, () => {
            this._actions.updateServiceEndpointAuthenticationScheme.invoke(EndpointSchemeConversionSuccessful);
        });
    }

    public stopMonitoringSchemeConversionProgress() {
        this._monitoredSchemeConversions.forEach((upgradeMonitor) => upgradeMonitor.cancel());
        this._monitoredSchemeConversions = [];
        this._actions.setMonitorSchemeConversionProgress.invoke(false);
    }

    private _monitorEndpointSchemeConversionProgress(endpointId: string, initialDelay: number, delayInterval: number, onSuccess: () => void = () => {}): void {
        const monitorUpgradeProcess = new DelayedFunction.DelayedFunction(this, initialDelay, "monitorEndpointUpgradeProgress", () => {
            this._source.getServiceEndpoint(endpointId).then((response: ServiceEndpoint) => {
                let operationStatus = { state: ServiceEndpointOperationStatus.InProgress, statusMessage: "" };
                if (response.operationStatus !== null && response.operationStatus !== undefined) {
                    operationStatus = response.operationStatus;
                }

                if (
                    operationStatus.state === ServiceEndpointOperationStatus.InProgress &&
                    Utils_String.equals(
                        operationStatus.statusMessage,
                        EndpointConstants.AzureEndpointConstants.ConvertAuthenticationSchemeInProgressStatus,
                        true
                    )
                ) {
                    monitorUpgradeProcess.setDelay(delayInterval);
                    monitorUpgradeProcess.reset();
                } else {
                    if (operationStatus.statusMessage === "") {
                        // trigger toast
                        onSuccess();
                    } else {
                        monitorUpgradeProcess.cancel();
                        this._actions.setMonitorSchemeConversionProgress.invoke(false);
                    }

                    // Update the entire page
                    this.getServiceEndpoint(response.id);
                }
            });
        });

        this._monitoredSchemeConversions.push(monitorUpgradeProcess);

        this._actions.setMonitorSchemeConversionProgress.invoke(true);
        monitorUpgradeProcess.start();
    }

    public updateServiceEndpointSharedFlag(serviceEndpoint: ServiceEndpoint) {
        this._actions.updateServiceEndpointSharedFlag.invoke(serviceEndpoint);
    }

    private _actions: ServiceEndpointActionsHub;
    private _source: ServiceEndpointsSource;
    private _monitoredSchemeConversions: DelayedFunction.DelayedFunction[];
}
